import React, { useState, useEffect } from 'react';
import {
  Container,
  Paper,
  TextField,
  Button,
  Typography,
  Box,
  Alert,
  CircularProgress,
  Card,
  CardContent,
  Divider
} from '@mui/material';
import { Login as LoginIcon, PersonAdd as RegisterIcon } from '@mui/icons-material';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import { useLanguage } from '../contexts/LanguageContext';

const Login = () => {
  const [isLogin, setIsLogin] = useState(true);
  const [formData, setFormData] = useState({
    username: '',
    email: '',
    password: '',
    confirmPassword: ''
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const { login, register, isAuthenticated } = useAuth();
  const navigate = useNavigate();
  const { t } = useLanguage();
  
  // Redirect if already authenticated
  useEffect(() => {
    if (isAuthenticated) {
      console.log('Already authenticated, redirecting to /app');
      // Use window.location to ensure browser URL changes to /app
      // This keeps us in the React app context (Firebase rewrites /app to /app/index.html)
      window.location.href = '/app';
    }
  }, [isAuthenticated]);

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
    setError('');
    setSuccess('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    setSuccess('');

    try {
      if (isLogin) {
        const result = await login(formData.username, formData.password);
        if (result.success) {
          console.log('Login successful!', result);
          setSuccess(t('login.loginSuccessful'));
          // Redirect immediately - don't wait for state to update
          // The login function already sets the user and token in AuthContext
          const currentPath = window.location.pathname;
          console.log('Login successful, current path:', currentPath);
          console.log('User from result:', result.user);
          
          // Redirect to /app using window.location
          // This ensures the browser URL changes to /app, which Firebase rewrites to /app/index.html
          // React Router will then see /app and match the /app route
          setTimeout(() => {
            console.log('Redirecting to /app after login');
            window.location.href = '/app';
          }, 100); // Small delay to ensure auth state is committed
        } else {
          setError(result.error);
        }
      } else {
        // Registration validation
        if (formData.password !== formData.confirmPassword) {
          setError(t('login.passwordsDoNotMatch'));
          setLoading(false);
          return;
        }

        if (formData.password.length < 6) {
          setError(t('login.passwordTooShort'));
          setLoading(false);
          return;
        }

        const result = await register({
          username: formData.username,
          email: formData.email,
          password: formData.password,
          role: 'user'
        });

        if (result.success) {
          setSuccess(t('login.registrationSuccessful'));
          setIsLogin(true);
          setFormData({
            username: '',
            email: '',
            password: '',
            confirmPassword: ''
          });
        } else {
          setError(result.error);
        }
      }
    } catch (error) {
      setError(t('login.unexpectedError'));
    } finally {
      setLoading(false);
    }
  };

  const toggleMode = () => {
    setIsLogin(!isLogin);
    setFormData({
      username: '',
      email: '',
      password: '',
      confirmPassword: ''
    });
    setError('');
    setSuccess('');
  };

  return (
    <Container component="main" maxWidth="sm">
      <Box
        sx={{
          marginTop: 8,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
        }}
      >
        <Card sx={{ width: '100%', maxWidth: 500 }}>
          <CardContent sx={{ p: 4 }}>
            <Box sx={{ textAlign: 'center', mb: 3 }}>
              <Typography
                component="h1"
                variant="h4"
                sx={{
                  fontWeight: 'bold',
                  color: 'primary.main',
                  mb: 1
                }}
              >
                {t('login.title')}
              </Typography>
              <Typography variant="h6" color="text.secondary">
                {isLogin ? t('login.signIn') : t('login.createAccount')}
              </Typography>
            </Box>

            {error && (
              <Alert severity="error" sx={{ mb: 2 }}>
                {error}
              </Alert>
            )}

            {success && (
              <Alert severity="success" sx={{ mb: 2 }}>
                {success}
              </Alert>
            )}

            <Box component="form" onSubmit={handleSubmit} sx={{ mt: 1 }}>
              <TextField
                margin="normal"
                required
                fullWidth
                id="username"
                label={t('login.username')}
                name="username"
                autoComplete="username"
                autoFocus
                value={formData.username}
                onChange={handleChange}
                disabled={loading}
              />

              {!isLogin && (
                <TextField
                  margin="normal"
                  required
                  fullWidth
                  id="email"
                  label={t('login.email')}
                  name="email"
                  autoComplete="email"
                  type="email"
                  value={formData.email}
                  onChange={handleChange}
                  disabled={loading}
                />
              )}

              <TextField
                margin="normal"
                required
                fullWidth
                name="password"
                label={t('login.password')}
                type="password"
                id="password"
                autoComplete="current-password"
                value={formData.password}
                onChange={handleChange}
                disabled={loading}
              />

              {!isLogin && (
                <TextField
                  margin="normal"
                  required
                  fullWidth
                  name="confirmPassword"
                  label={t('login.confirmPassword')}
                  type="password"
                  id="confirmPassword"
                  value={formData.confirmPassword}
                  onChange={handleChange}
                  disabled={loading}
                />
              )}

              <Button
                type="submit"
                fullWidth
                variant="contained"
                sx={{ mt: 3, mb: 2, py: 1.5 }}
                disabled={loading}
                startIcon={loading ? <CircularProgress size={20} /> : (isLogin ? <LoginIcon /> : <RegisterIcon />)}
              >
                {loading ? t('common.processing') : (isLogin ? t('login.signIn') : t('login.createAccount'))}
              </Button>

              <Divider sx={{ my: 2 }} />

              <Button
                fullWidth
                variant="outlined"
                onClick={toggleMode}
                disabled={loading}
                sx={{ py: 1 }}
              >
                {isLogin 
                  ? t('login.dontHaveAccount')
                  : t('login.alreadyHaveAccount')
                }
              </Button>
            </Box>

            {isLogin && (
              <Box sx={{ mt: 3, p: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
                <Typography variant="body2" color="text.secondary" align="center">
                  <strong>{t('login.demoAccounts')}:</strong><br />
                  {t('login.demoAdmin')}<br />
                  {t('login.demoUser')}
                </Typography>
              </Box>
            )}
          </CardContent>
        </Card>
      </Box>
    </Container>
  );
};

export default Login;
